// AIBOX-03：一筆指派的模型設定——在那一路的即時畫面上點出偵測區域 / 拉出越線，調靈敏度與排程。
// 座標存正規化值（0..1），跟解析度無關；存檔走 PATCH …/assignments/{id} {models}（CAS）。形狀見 zones.ts。
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KinioError } from '@kinio/web-sdk';
import { Dialog } from '../../components/Dialog';
import { aiApi, type AiAssignment } from '../../lib/planeApi';
import type { VmsDevice } from '../../lib/devices';
import { LiveTile } from '../live/LiveTile';
import { aiModelName } from './modelName';
import { ZONE_MAX, checkParams, cleanParams, modelUses, nearestVertex, toNorm, type ModelParams, type Pt } from './zones';

const DAYS = [1, 2, 3, 4, 5, 6, 0];

export function AssignmentSettings(props: {
  api: string;
  homeId: string;
  assignment: AiAssignment;
  cam?: VmsDevice;
  camName: string;
  close(): void;
  done(): void;
}) {
  const { t } = useTranslation();
  const { assignment: a } = props;
  const [idx, setIdx] = useState(0);
  const [params, setParams] = useState<ModelParams[]>(() => a.models.map((m) => ({ ...(m.params as ModelParams | undefined) })));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const drag = useRef<number | null>(null);
  const model = a.models[idx]?.model ?? '';
  const uses = modelUses(model);
  const p = params[idx] ?? {};
  const set = (patch: Partial<ModelParams>) => setParams((l) => l.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  const at = (e: React.PointerEvent): Pt => {
    const r = box.current!.getBoundingClientRect();
    return toNorm(e.clientX - r.left, e.clientY - r.top, r.width, r.height);
  };
  const zone = p.zone ?? [];
  const line = p.line;

  const down = (e: React.PointerEvent) => {
    const pt = at(e);
    if (uses.line) {
      // 越線：第一下起點、第二下終點；之後再點 = 重畫
      if (!line || (line[0] !== line[1] && !e.shiftKey)) set({ line: [pt, pt] });
      else set({ line: [line[0], pt] });
      return;
    }
    const i = nearestVertex(zone, pt);
    if (i >= 0) drag.current = i;
    else if (zone.length < ZONE_MAX) set({ zone: [...zone, pt] });
  };
  const move = (e: React.PointerEvent) => {
    if (drag.current === null || uses.line) return;
    const pt = at(e);
    set({ zone: zone.map((q, i) => (i === drag.current ? pt : q)) });
  };
  const pending = uses.line && line && line[0] === line[1];

  async function save() {
    for (let i = 0; i < a.models.length; i++) {
      const bad = checkParams(a.models[i].model, cleanParams(a.models[i].model, params[i] ?? {}));
      if (bad) {
        setIdx(i);
        setErr(t(`aiset.err_${bad}`));
        return;
      }
    }
    setBusy(true);
    setErr(null);
    try {
      await aiApi.update(props.api, props.homeId, a.assignment_id, {
        expected_version: a.version,
        models: a.models.map((m, i) => ({ model: m.model, params: cleanParams(m.model, params[i] ?? {}) })),
      });
      props.done();
    } catch (e) {
      setErr(e instanceof KinioError ? t(`ai.err_${e.code}`, { defaultValue: t('common.op_failed', { code: e.code }) }) : String(e));
    } finally {
      setBusy(false);
    }
  }

  const sched = p.schedule;
  return (
    <Dialog open title={t('aiset.title', { name: props.camName })} onClose={props.close} wide>
      <div className="form">
        {a.models.length > 1 && (
          <div className="seg" role="tablist">
            {a.models.map((m, i) => (
              <button key={m.model} role="tab" aria-pressed={i === idx} onClick={() => setIdx(i)}>
                {aiModelName(t, m.model)}
              </button>
            ))}
          </div>
        )}
        <p className="muted small">{uses.line ? t('aiset.line_help') : t('aiset.zone_help', { max: ZONE_MAX })}</p>
        <div className="aiset-stage">
          {props.cam ? <LiveTile cam={props.cam} label={props.camName} stream="sub" /> : <div className="tile tile-empty" />}
          <div
            ref={box}
            className="aiset-draw"
            data-testid="aiset-draw"
            onPointerDown={down}
            onPointerMove={move}
            onPointerUp={() => (drag.current = null)}
            onPointerLeave={() => (drag.current = null)}
          >
            <svg viewBox="0 0 1 1" preserveAspectRatio="none">
              {!uses.line && zone.length >= 2 && <polygon points={zone.map((q) => q.join(',')).join(' ')} className={zone.length >= 3 ? 'ok' : ''} />}
              {uses.line && line && <line x1={line[0][0]} y1={line[0][1]} x2={line[1][0]} y2={line[1][1]} />}
            </svg>
            {(uses.line ? (line ?? []) : zone).map((q, i) => (
              <span key={i} className="aiset-pt" style={{ left: `${q[0] * 100}%`, top: `${q[1] * 100}%` }}>
                {uses.line ? 'AB'[i] : ''}
              </span>
            ))}
          </div>
        </div>
        <div className="toolbar">
          {!uses.line && (
            <>
              <span className="small muted" data-testid="aiset-count">
                {zone.length ? t('aiset.zone_points', { n: zone.length }) : t('aiset.zone_full')}
              </span>
              <button className="btn btn-sm" disabled={!zone.length} onClick={() => set({ zone: zone.slice(0, -1) })}>
                {t('aiset.undo')}
              </button>
              <button className="btn btn-sm" disabled={!zone.length} onClick={() => set({ zone: [] })}>
                {t('aiset.clear')}
              </button>
            </>
          )}
          {uses.line && (
            <>
              <span className="small muted">{pending ? t('aiset.line_second') : line ? t('aiset.line_set') : t('aiset.line_first')}</span>
              <label className="small">
                {t('aiset.direction')}{' '}
                <select
                  className="input"
                  value={p.direction ?? 'both'}
                  onChange={(e) => set({ direction: e.target.value as ModelParams['direction'] })}
                  aria-label={t('aiset.direction')}
                >
                  <option value="both">{t('aiset.dir_both')}</option>
                  <option value="a_to_b">{t('aiset.dir_a_to_b')}</option>
                  <option value="b_to_a">{t('aiset.dir_b_to_a')}</option>
                </select>
              </label>
              <button className="btn btn-sm" disabled={!line} onClick={() => set({ line: undefined })}>
                {t('aiset.clear')}
              </button>
            </>
          )}
        </div>
        <label className="field">
          <span>{t('ai.sensitivity', { n: p.sensitivity ?? 60 })}</span>
          <input
            type="range"
            min={10}
            max={100}
            step={10}
            value={p.sensitivity ?? 60}
            onChange={(e) => set({ sensitivity: Number(e.target.value) })}
            aria-label={t('ai.sensitivity', { n: p.sensitivity ?? 60 })}
          />
        </label>
        <fieldset className="field">
          <legend>{t('aiset.schedule')}</legend>
          <label className="check">
            <input
              type="checkbox"
              checked={!sched}
              onChange={(e) => set({ schedule: e.target.checked ? undefined : { days: [0, 1, 2, 3, 4, 5, 6], start: '22:00', end: '06:00' } })}
            />
            {t('aiset.always')}
          </label>
          {sched && (
            <>
              <div className="check-grid">
                {DAYS.map((d) => (
                  <label key={d} className="check">
                    <input
                      type="checkbox"
                      checked={sched.days.includes(d)}
                      onChange={() => set({ schedule: { ...sched, days: sched.days.includes(d) ? sched.days.filter((x) => x !== d) : [...sched.days, d] } })}
                    />
                    {t(`aiset.day_${d}`)}
                  </label>
                ))}
              </div>
              <div className="toolbar">
                <input
                  className="input"
                  type="time"
                  value={sched.start}
                  aria-label={t('aiset.start')}
                  onChange={(e) => set({ schedule: { ...sched, start: e.target.value } })}
                />
                <span>–</span>
                <input
                  className="input"
                  type="time"
                  value={sched.end}
                  aria-label={t('aiset.end')}
                  onChange={(e) => set({ schedule: { ...sched, end: e.target.value } })}
                />
                <span className="muted small">{t('aiset.schedule_help')}</span>
              </div>
            </>
          )}
        </fieldset>
        {err && <p className="error">{err}</p>}
        <div className="toolbar">
          <button className="btn btn-primary" disabled={busy} onClick={() => void save()}>
            {t('common.save')}
          </button>
          <button className="btn" onClick={props.close}>
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
